// Pure stats helpers — UI builders and aggregation utilities.
//
// These render small DOM fragments (sparkline, type-bar, player list, stat
// card) or compute aggregates from the raw event payloads. They have no side
// effects and don't reach into `state` directly; callers pass in everything
// they need.
//
// `pickTeamMvp` selects the MVP from the winning team for a team-mode event
// using a multi-key sort (win-rate → wins → goal diff). Used in result-share
// and event-completion screens.

import { h as preactH } from "preact";
import { SURFACE_OPTIONS, SURFACE_LABELS } from "../ui/format";
import { matchPlayerName } from "../matches";
import { mountPreact } from "../ui/preact-mount";
import { StatCard } from "../ui/components/StatCard";
import { TypeBar } from "../ui/components/TypeBar";
import { PlayerList } from "../ui/components/PlayerList";
import { MonthlySparkline } from "../ui/components/MonthlySparkline";

// Week 2 Step 1·2: stats 도메인의 leaf 컴포넌트들을 Preact JSX로 전환.
// 시그니처는 동일(HTMLElement 반환). 내부적으로 mountPreact가 JSX를
// display:contents wrapper 안에 렌더링해 호출부는 변경 불필요.
export function statCard(label: string, value: number | string | null | undefined): HTMLElement {
  return mountPreact(preactH(StatCard, { label, value }));
}

export interface MonthlyEntry {
  wins: number;
  losses: number;
  draws?: number;
}

export function buildMonthlySparkline(entries: Array<[string, MonthlyEntry]>): HTMLElement {
  return mountPreact(preactH(MonthlySparkline, { entries }));
}

export interface TypeBuckets {
  mixed: number;
  men: number;
  women: number;
  exceptional: number;
}

export function buildTypeBar(types: TypeBuckets, total: number): HTMLElement {
  return mountPreact(preactH(TypeBar, { types, total }));
}

export interface PlayerRow {
  name: string;
  tag?: string | null;
  total: number;
  wins: number;
  losses: number;
  draws?: number;
}

export function buildPlayerList(rows: PlayerRow[]): HTMLElement {
  return mountPreact(preactH(PlayerList, { rows }));
}

export interface SurfaceAggregate {
  surface: string;
  label: string;
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  decided: number;
}

export function aggregateSurfaceStats(summaries: any[]): SurfaceAggregate[] {
  const acc: Record<string, { matches: number; wins: number; losses: number; draws: number }> = {};
  for (const s of summaries) {
    const ss = s.surface_stats;
    if (!ss || typeof ss !== "object") continue;
    for (const [surface, bucket] of Object.entries(ss as Record<string, any>)) {
      if (!bucket || !SURFACE_LABELS[surface]) continue;
      if (!acc[surface]) acc[surface] = { matches: 0, wins: 0, losses: 0, draws: 0 };
      acc[surface].matches += Number(bucket.completed_matches || 0);
      acc[surface].wins += Number(bucket.wins || 0);
      acc[surface].losses += Number(bucket.losses || 0);
      acc[surface].draws += Number(bucket.draws || 0);
    }
  }
  const order = SURFACE_OPTIONS.map(([v]) => v);
  return order
    .filter((v) => acc[v] && acc[v].matches > 0)
    .map((v) => ({
      surface: v,
      label: SURFACE_LABELS[v],
      matches: acc[v].matches,
      wins: acc[v].wins,
      losses: acc[v].losses,
      draws: acc[v].draws,
      decided: acc[v].wins + acc[v].losses,
    }));
}

interface PlayerMvpStats {
  name: string;
  wins: number;
  losses: number;
  draws: number;
  scored: number;
  conceded: number;
}

export function pickTeamMvp(detail: any): (PlayerMvpStats & { pid: string }) | null {
  const matches = (detail.matches || []).filter((m: any) => m.winner_side);
  if (matches.length === 0) return null;
  const teams = detail.teams || [];
  const ev = detail.event || {};
  const isFriendly = !!ev.is_friendly_match;
  const teamStats = new Map<string, { wins: number; scored: number; conceded: number }>();
  const playerStats = new Map<string, PlayerMvpStats>();
  for (const t of teams) teamStats.set(t.team_id, { wins: 0, scored: 0, conceded: 0 });
  for (const p of detail.participants || []) {
    playerStats.set(p.event_participant_id, {
      name: matchPlayerName(p),
      wins: 0,
      losses: 0,
      draws: 0,
      scored: 0,
      conceded: 0,
    });
  }
  for (const m of matches) {
    const sideA = (m.sides || []).find((s: any) => s.side_number === 1);
    const sideB = (m.sides || []).find((s: any) => s.side_number === 2);
    if (!sideA || !sideB) continue;
    const scoreA = Number(m.side_a_score) || 0;
    const scoreB = Number(m.side_b_score) || 0;
    const bumpTeam = (id: string, win: boolean, s: number, c: number) => {
      if (!id || !teamStats.has(id)) return;
      const r = teamStats.get(id)!;
      if (win) r.wins++;
      r.scored += s;
      r.conceded += c;
    };
    bumpTeam(sideA.team_id, m.winner_side === "side_a", scoreA, scoreB);
    bumpTeam(sideB.team_id, m.winner_side === "side_b", scoreB, scoreA);
    const bumpPlayers = (
      pids: string[],
      win: boolean,
      draw: boolean,
      s: number,
      c: number,
    ) => {
      for (const pid of pids || []) {
        if (!playerStats.has(pid)) continue;
        const r = playerStats.get(pid)!;
        if (draw) r.draws++;
        else if (win) r.wins++;
        else r.losses++;
        r.scored += s;
        r.conceded += c;
      }
    };
    bumpPlayers(
      sideA.player_ids,
      m.winner_side === "side_a",
      m.winner_side === "draw",
      scoreA,
      scoreB,
    );
    bumpPlayers(
      sideB.player_ids,
      m.winner_side === "side_b",
      m.winner_side === "draw",
      scoreB,
      scoreA,
    );
  }
  // 클럽 트렌드는 우리 클럽 관점이라 상대 클럽원을 MVP로 띄우면 안 된다.
  // 교류전(friendly)에서는 호스트팀(team_order=1)이 단독 1위일 때만 MVP를 뽑고,
  // 패배·무승부 시에는 표시하지 않는다 — "MVP" 톤이 패배 결과와 어울리지 않기 때문.
  const ranked = [...teamStats.entries()]
    .map(([id, s]) => ({ id, ...s, diff: s.scored - s.conceded }))
    .sort((a, b) => b.wins - a.wins || b.diff - a.diff);
  const winningTeam = ranked[0];
  if (!winningTeam) return null;
  let team: any;
  if (isFriendly) {
    const host = teams.find((t: any) => t.team_order === 1);
    if (!host) return null;
    if (winningTeam.id !== host.team_id) return null;
    const second = ranked[1];
    if (second && second.wins === winningTeam.wins && second.diff === winningTeam.diff) return null;
    team = host;
  } else {
    team = teams.find((t: any) => t.team_id === winningTeam.id);
  }
  const candidatePids: string[] = team?.participant_ids || [];
  const mvp = candidatePids
    .map((pid) => ({ pid, ...(playerStats.get(pid) || ({} as PlayerMvpStats)) }))
    .filter((r: any) => r.wins + r.losses + r.draws > 0)
    .sort((a: any, b: any) => {
      const at = a.wins + a.losses + a.draws;
      const bt = b.wins + b.losses + b.draws;
      const ra = at ? a.wins / at : 0;
      const rb = bt ? b.wins / bt : 0;
      if (rb !== ra) return rb - ra;
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.scored - b.conceded - (a.scored - a.conceded);
    })[0];
  return (mvp as PlayerMvpStats & { pid: string }) || null;
}
